import React from 'react';
import { useParams } from 'react-router-dom';
import { useLanguage } from '../App';
import { useCoachChat } from '../hooks/useCoachChat';
import { usePhotoConsent } from '../hooks/usePhotoConsent';
import { PhotoConsentDialog } from '../components/PhotoConsentDialog';
import { ChatHeader } from '../components/coach/ChatHeader';
import { ChatEmptyState } from '../components/coach/ChatEmptyState';
import { ChatMessage, ChatTypingIndicator } from '../components/coach/ChatMessage';
import { ChatComposer, PhotoPreviewChip } from '../components/coach/ChatComposer';

export default function CoachChatPage() {
  const { sessionId: urlSessionId } = useParams();
  const { language } = useLanguage();
  const {
    messages, input, setInput, loading, loadingMessages, sendingPhoto, selectedPhoto, photoPreview, messagesContainerRef,
    handleSend, selectPhoto, clearPhoto, startNewSession,
  } = useCoachChat(urlSessionId, language);
  const { consentOpen, onConsentOpenChange, onConsentAccepted, requireConsent, retentionDays } = usePhotoConsent();

  // Photo messages need the photo-usage consent (asked once, before the first analysis)
  const handleSendWithConsent = () => (selectedPhoto ? requireConsent(handleSend) : handleSend());

  const renderMessages = () => {
    if (loadingMessages) {
      return (
        <div className="flex justify-center py-12" data-testid="chat-loading">
          <div className="w-6 h-6 border-2 border-[var(--lux-gold)] border-t-transparent rounded-full animate-spin" />
        </div>
      );
    }
    if (messages.length === 0) {
      return <ChatEmptyState language={language} onSuggestion={setInput} />;
    }
    return messages.map(msg => <ChatMessage key={msg.id} message={msg} />);
  };

  return (
    <div className="flex flex-col h-[calc(100dvh-49px-80px)] md:h-[calc(100vh-49px)]" data-testid="coach-chat-page">
      <ChatHeader language={language} onNewSession={startNewSession} />

      <div ref={messagesContainerRef} className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 space-y-4" data-testid="chat-messages">
        {renderMessages()}
        {loading && <ChatTypingIndicator language={language} analyzing={sendingPhoto} />}
      </div>

      {photoPreview && <PhotoPreviewChip src={photoPreview} onClear={clearPhoto} />}

      <ChatComposer
        language={language}
        input={input}
        setInput={setInput}
        loading={loading}
        canSend={Boolean(input.trim() || selectedPhoto)}
        onSend={handleSendWithConsent}
        onSelectPhoto={selectPhoto}
      />
      <PhotoConsentDialog
        open={consentOpen}
        onOpenChange={onConsentOpenChange}
        onAccepted={onConsentAccepted}
        language={language}
        retentionDays={retentionDays}
      />
    </div>
  );
}
